import React, { useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { fetchIndentsAsync, fetchDropdownDataAsync, setFilteredIndents } from '../../../store/peindentSlice';

const LoadingSpinner = React.memo(() => (
  <div className="flex justify-center items-center p-8">
    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
  </div>
));

const Indent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { role, id: enteredBy } = useSelector((state) => state.auth);
  const { 
    indents, 
    filteredIndents, 
    dropdownData, 
    loading: { indents: isLoading, dropdown: dropdownLoading },
    error 
  } = useSelector((state) => state.peindent); // Changed from state.indent to state.peindent
  
  const [selectedRow, setSelectedRow] = React.useState(null);

  useEffect(() => {
    dispatch(fetchIndentsAsync({ role, enteredBy }));
    dispatch(fetchDropdownDataAsync());
  }, [dispatch, role, enteredBy]);

  const getEditFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData?.projects?.map(project => ({
        value: project.id,
        label: project.name,
      })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData?.types?.map(type => ({
        value: type.id,
        label: type.name,
      })) || [],
    },
  ], [dropdownData?.projects, dropdownData?.types]);

  const handleSearch = useCallback((query) => {
    if (!query) {
      dispatch(setFilteredIndents(indents));
      return;
    }
    
    const lowercaseQuery = query.toLowerCase();
    const filtered = indents.filter((indent) => 
      indent.IndentID.toString().toLowerCase().includes(lowercaseQuery) ||
      indent.Status?.toLowerCase().includes(lowercaseQuery) ||
      indent.ProjectName?.toLowerCase().includes(lowercaseQuery) ||
      indent.TypeName?.toLowerCase().includes(lowercaseQuery) ||
      indent.CreationDate?.toString().toLowerCase().includes(lowercaseQuery)
    );
    dispatch(setFilteredIndents(filtered));
  }, [indents, dispatch]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (!dateFrom || !dateTo) {
      dispatch(setFilteredIndents(indents));
      return;
    }

    const filtered = indents.filter((indent) => {
      const creationDate = new Date(indent.CreationDate);
      return creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo);
    });
    dispatch(setFilteredIndents(
      filtered.sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate))
    ));
  }, [indents, dispatch]);

  const handleRowClick = useCallback((row) => {
    navigate(`/PETable`, { state: { row } });
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (isLoading || dropdownLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">PE Table</h3>
        </div>

        {dropdownData?.users?.length > 0 && dropdownData?.projects?.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields}
            apiEndpoint={`${process.env.REACT_APP_API_BASE_URL}/indent/indent`}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={() => dispatch(fetchIndentsAsync({ role, enteredBy }))}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            addLink="/addIndent"
            editRoute="/PETable"
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Schedule" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onDoubleClick={handleRowClick}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-blue-100"
            noDataMessage="No indents found."
            userRole={role}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default React.memo(Indent);